import React, { useEffect, useRef, useState } from "react";
import PracticeReticle from "./PracticeReticle";
import ShutterTrail from "./ShutterTrail";

const sceneBackground = (room) => {
    switch (room.id) {
        case "antechamber":
            return "radial-gradient(ellipse at 50% 65%, rgba(255, 59, 0, 0.55) 0%, rgba(40, 5, 0, 0.9) 45%, #050303 85%)";
        case "running_shadow":
            return "linear-gradient(180deg, #0a0606 0%, #1a0c0a 60%, #050303 100%)";
        case "locked_cabinet":
            return "radial-gradient(ellipse at 50% 50%, rgba(255, 240, 200, 0.18) 0%, rgba(15, 10, 6, 0.95) 70%)";
        case "emergency_strobe":
            return "#050303";
        case "phantom":
            return "radial-gradient(ellipse at 50% 60%, rgba(138, 3, 3, 0.65) 0%, #000 75%)";
        default:
            return "#050303";
    }
};

// Speed of the "running shadow" — reacts to shutter so students SEE motion blur.
const shadowRunnerDuration = (shutterStops) => {
    if (shutterStops >= 10) return "0.8s";
    if (shutterStops >= 8)  return "1.4s";
    if (shutterStops >= 6)  return "2.6s";
    if (shutterStops >= 4)  return "4.5s";
    return "7s";
};

const evBadgeStyle = (tone) => {
    if (tone === "ok")   return { color: "#000", background: "var(--crt-green)", boxShadow: "0 0 10px var(--crt-green)" };
    if (tone === "warn") return { color: "#000", background: "var(--amber)",     boxShadow: "0 0 10px var(--amber)" };
    return { color: "#FFF0E6", background: "var(--phantom-red)", boxShadow: "0 0 10px var(--phantom-red)" };
};

export const Viewfinder = ({ room, meters, fx, flashState, captured, previewSettings, livePreviewFx, practiceMode }) => {
    // Correct-exposure green pulse — bumps a key whenever evTone flips TO "ok"
    // so the CSS keyframe re-triggers even on repeat OKs. `pulseVisible` unmounts
    // the overlay once the 780ms animation is done so no stale nodes linger.
    const [okPulseKey, setOkPulseKey] = useState(0);
    const [pulseVisible, setPulseVisible] = useState(false);
    const prevTone = useRef(null);
    useEffect(() => {
        const tone = livePreviewFx?.evTone;
        if (!captured && tone === "ok" && prevTone.current !== "ok") {
            setOkPulseKey((k) => k + 1);
            setPulseVisible(true);
        }
        prevTone.current = tone;
    }, [livePreviewFx?.evTone, captured]);
    useEffect(() => {
        if (!pulseVisible) return undefined;
        const t = setTimeout(() => setPulseVisible(false), 820);
        return () => clearTimeout(t);
    }, [okPulseKey, pulseVisible]);

    // When capture is in progress → use post-capture fx from evaluateShot for the finished image.
    // Before capture → use livePreviewFx so the scene reflects the dials in real time.
    const live = !captured && livePreviewFx ? livePreviewFx : null;
    const brightness = live ? live.brightness : (fx?.brightness ?? 1);
    // When previewing live, blur is already computed inside computeLivePreviewFx.
    // After capture, fall back to the graded fx.blur + meter reading.
    const totalBlur = live
        ? Math.round(live.motionBlur)
        : Math.round((fx?.blur ?? 0) + (meters?.blur ?? 0) * 2);
    const noiseOpacity = live ? live.noise : (meters?.noise ?? 0);
    const tint = live?.tint;
    const bgBlur = live ? live.bgBlur : 0;

    return (
        <div
            className="relative aspect-[4/3] w-full overflow-hidden border-2 border-amber-30 grain crt-vignette viewfinder-brackets"
            style={{ background: "#000" }}
            data-testid="viewfinder"
            aria-label={`Viewfinder: ${room.name}`}
            role="img"
        >
            {/* Scene */}
            <div
                className={`absolute inset-0 transition-[filter,opacity] duration-300 ${room.id === "emergency_strobe" ? "scene-strobe" : ""}`}
                style={{
                    background: sceneBackground(room),
                    filter: `blur(${totalBlur}px) brightness(${brightness})`,
                    opacity: captured ? 0.4 : 1,
                }}
            >
                {/* Per-room atmospheric elements */}
                {room.id === "antechamber" && (
                    <div className="absolute inset-0 safelight" style={{ background: "radial-gradient(circle at 50% 30%, rgba(255,59,0,0.35), transparent 60%)" }} />
                )}
                {room.id === "running_shadow" && (
                    <div
                        className="shadow-runner"
                        style={{ animationDuration: shadowRunnerDuration(previewSettings?.shutter?.stops ?? 6) }}
                    />
                )}
                {room.id === "locked_cabinet" && (
                    <>
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-amber font-display text-3xl z-[2]" style={{ textShadow: "0 0 12px var(--amber)" }}>
                            🔑
                        </div>
                        <div className="absolute inset-0" style={{
                            background: "repeating-linear-gradient(90deg, transparent 0 30px, rgba(255,240,200,0.06) 30px 60px)",
                            filter: `blur(${bgBlur}px)`,
                            transition: "filter 200ms ease",
                        }} />
                    </>
                )}
                {room.id === "phantom" && (
                    <div className="phantom-silhouette" />
                )}
            </div>

            {/* Colour tint overlay for over/under exposure */}
            {tint === "under" && (
                <div
                    className="absolute inset-0 pointer-events-none transition-opacity duration-200"
                    style={{ background: "rgba(120, 20, 0, 0.35)", mixBlendMode: "multiply" }}
                    data-testid="tint-under"
                    aria-hidden="true"
                />
            )}
            {tint === "over" && (
                <div
                    className="absolute inset-0 pointer-events-none transition-opacity duration-200"
                    style={{ background: "rgba(255, 245, 220, 0.55)", mixBlendMode: "screen" }}
                    data-testid="tint-over"
                    aria-hidden="true"
                />
            )}

            {/* Highlight-warning zebras — real cinema cue for blown highlights */}
            {tint === "over" && (
                <div
                    className="zebra-overlay"
                    style={{ opacity: Math.min(1, 0.5 + Math.abs(live?.evDiff ?? 2) * 0.08) }}
                    data-testid="zebra-overlay"
                    data-diff={live?.evDiff}
                    aria-hidden="true"
                />
            )}

            {/* Noise overlay (live) */}
            {noiseOpacity > 0.05 && (
                <div
                    className="scene-noise absolute inset-0"
                    style={{ opacity: noiseOpacity }}
                    data-testid="viewfinder-noise"
                />
            )}

            {/* OK-exposure green pulse — re-keyed so the animation replays on each fresh OK */}
            {pulseVisible && live?.evTone === "ok" && (
                <div
                    key={okPulseKey}
                    className="ok-pulse-overlay"
                    data-testid="viewfinder-ok-pulse"
                    aria-hidden="true"
                />
            )}

            {/* Corner brackets + reticle */}
            <span className="bracket bracket-tl" />
            <span className="bracket bracket-tr" />
            <span className="bracket bracket-bl" />
            <span className="bracket bracket-br" />
            <span className="reticle" />

            {/* Shutter-speed light trails when the shutter is too slow to freeze motion */}
            {!captured && live?.shutterExcess > 0 && (
                <ShutterTrail excess={live.shutterExcess} />
            )}

            {/* Practice-mode ghost target — only during practice so we don't spoil the game */}
            {!captured && practiceMode && live && (
                <PracticeReticle inRange={!!live.inRange} evTone={live.evTone} />
            )}

            {/* HUD overlay */}
            <div className="absolute top-3 left-12 right-12 flex justify-between items-center text-[10px] tracking-[0.25em] font-mono uppercase z-[4]">
                <span
                    className="text-amber px-1"
                    style={{ textShadow: "0 0 6px #000, 0 1px 2px #000" }}
                    data-testid="viewfinder-room-name"
                >
                    ▶ {room.name}
                </span>
                {live && (
                    <span
                        className="px-2 py-0.5 font-bold tracking-[0.2em] text-[10px]"
                        style={evBadgeStyle(live.evTone)}
                        data-testid="viewfinder-ev-badge"
                        aria-label={`Exposure preview ${live.evLabel}`}
                    >
                        {live.evLabel}
                    </span>
                )}
                <span
                    className="text-[var(--text-secondary)]"
                    style={{ textShadow: "0 0 6px #000, 0 1px 2px #000" }}
                >
                    REC ● 35MM
                </span>
            </div>

            {/* Bottom info bar */}
            <div className="absolute bottom-3 left-12 right-12 flex justify-between text-[10px] tracking-[0.2em] text-amber font-mono uppercase z-[4]">
                <span data-testid="viewfinder-settings" style={{ textShadow: "0 0 6px #000, 0 1px 2px #000" }}>
                    {previewSettings?.aperture?.label} · {previewSettings?.shutter?.label} · ISO {previewSettings?.iso?.label}
                </span>
                <span style={{ textShadow: "0 0 6px #000, 0 1px 2px #000" }}>ROOM {room.index + 1}/5</span>
            </div>

            {/* Flash overlay */}
            {flashState === "white" && <div className="flash-overlay flash-white" />}
            {flashState === "black" && <div className="flash-overlay flash-black" />}
        </div>
    );
};

export default Viewfinder;
